import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
	PolarAngleAxis,
	PolarGrid,
	PolarRadiusAxis,
	Radar,
	RadarChart,
	ResponsiveContainer,
} from 'recharts';
import { PageTitle } from '@/components/ui/PageTitle';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Tooltip,
} from '@/components/ui';
import { cn } from '@/lib/utils';
import { ArrowLeft, ChevronDown, Download, Info } from 'lucide-react';
import { DashboardDepartmentPickerModal } from '@/pages/admin/companies/components/company-dashboard/DashboardDepartmentPickerModal';
import { SelectionRowTrigger } from '@/pages/admin/companies/components/company-dashboard/SelectionRowTrigger';

const COPY = {
	resumo: {
		indicatorsSectionTitle: 'Indicadores',
		participacao: { cardTitle: 'Participação' },
		riskFactors: {
			title: 'Fatores de risco',
			critical: { label: 'críticos' },
			attention: { label: 'atenção' },
			ok: { label: 'ok' },
		},
		burnoutCbi: {
			title: 'Índice de esgotamento (CBI)',
			scaleMax: 100,
			scaleHint:
				'Neste índice, maior = melhor: ≥50 risco baixo · 26–49 risco moderado · ≤25 risco alto.',
		},
	},
	perfil: {
		sectionTitle: 'Perfil psicossocial',
		visionByDomain: {
			kicker: 'Perfil psicossocial — COPSOQ II-BR',
			cardTitle: 'Visão por Domínio',
			subtitle:
				'Score médio por área (1-5): Mais alto = melhor · mais baixo = pior\nPara dentro da linha tracejada = atenção · Fora da linha = melhor',
		},
		cbi: {
			title: 'Esgotamento — CBI',
			subtitle: 'Copenhagen Burnout Inventory — 3 dimensões · 0–100 pts',
		},
	},
	mapaCalor: {
		sectionTitle: 'Mapa de Calor',
		blockTitle: 'Nota mais alta = melhor',
		legendFavorable: 'Verde',
		legendAttention: 'Amarelo',
		legendCritical: 'Vermelho',
	},
} as const;

const ALL_DEPARTMENT_ID = 'all' as const;
type DeptId = typeof ALL_DEPARTMENT_ID | string;

const MOCK_MONTHS = [
	{ monthId: '2026-04', label: 'abril de 2026' },
	{ monthId: '2026-03', label: 'março de 2026' },
] as const;

const MOCK_DEPARTMENTS: CompanyDepartments.CompanyDepartment[] = [
	{
		id: 'dept-1',
		name: 'Operações',
		created_at: '',
		updated_at: '',
	},
	{
		id: 'dept-2',
		name: 'People & Cultura',
		created_at: '',
		updated_at: '',
	},
];

const COPSOQ_ACCENT = '#e11d74';
/** Limiar de atenção — escala invertida (6 − 3,66). */
const COPSOQ_ATTENTION_THRESHOLD = 2.34;

/** Legenda do mapa de calor — escala 0–100, maior = melhor. */
const HEAT_LEGEND = {
	favorable: {
		swatchClass: 'border-emerald-200 bg-emerald-100',
		range: '> 67',
	},
	attention: {
		swatchClass: 'border-amber-200 bg-amber-100',
		range: '33 – 67',
	},
	critical: {
		swatchClass: 'border-rose-300 bg-rose-100',
		range: '< 33',
	},
} as const;

type HeatCellBand = 'favorable' | 'attention' | 'critical';

function formatHeatMapScore(value: number) {
	const display = Number.isInteger(value)
		? String(value)
		: String(Math.round(value * 10) / 10);
	return `${display}/100`;
}

function copsoqHeatCellClass(band: HeatCellBand) {
	if (band === 'favorable') {
		return 'border-emerald-200 bg-emerald-50/95 text-emerald-950 shadow-sm';
	}
	if (band === 'attention') {
		return 'border-amber-200 bg-amber-50/95 text-amber-950 shadow-sm';
	}
	return 'border-rose-300 bg-rose-50/95 text-rose-950 shadow-sm';
}

type HeatMapSortMode = 'criticalFirst' | 'favorableFirst';

const HEAT_MAP_SORT_LABELS: Record<HeatMapSortMode, string> = {
	criticalFirst: 'Ordenação: críticos primeiro',
	favorableFirst: 'Ordenação: favoráveis primeiro',
};

type DemoHeatRow = {
	dim: {
		id: string;
		shortLabel: string;
		polarity: 'demand' | 'resource';
		groupLabel: string;
	};
	raw: number;
	stressEq: number;
	band: HeatCellBand;
};

function sortHeatMapRows(
	rows: readonly DemoHeatRow[],
	mode: HeatMapSortMode,
): DemoHeatRow[] {
	const stable = rows.map((row, idx) => ({ row, idx }));
	return stable
		.sort((a, b) => {
			const cmp =
				mode === 'criticalFirst'
					? a.row.raw - b.row.raw
					: b.row.raw - a.row.raw;
			if (cmp !== 0) return cmp;
			return a.idx - b.idx;
		})
		.map((x) => x.row);
}

/** Mapa de calor unificado — escala 0–100, maior = melhor. */
const MOCK_HEAT_MAP_BASE: DemoHeatRow[] = [
	{
		dim: {
			id: 's1',
			shortLabel: 'Demandas quantitativas',
			polarity: 'demand',
			groupLabel: 'Trabalho',
		},
		raw: 27.5,
		stressEq: 97.5,
		band: 'critical',
	},
	{
		dim: {
			id: 's2',
			shortLabel: 'Ritmo de trabalho',
			polarity: 'demand',
			groupLabel: 'Trabalho',
		},
		raw: 40,
		stressEq: 85,
		band: 'attention',
	},
	{
		dim: {
			id: 's3',
			shortLabel: 'Conflito trabalho–família',
			polarity: 'demand',
			groupLabel: 'Pessoal',
		},
		raw: 47.5,
		stressEq: 77.5,
		band: 'attention',
	},
	{
		dim: {
			id: 's4',
			shortLabel: 'Incerteza organizacional',
			polarity: 'demand',
			groupLabel: 'Trabalho',
		},
		raw: 70,
		stressEq: 55,
		band: 'favorable',
	},
	{
		dim: {
			id: 's5',
			shortLabel: 'Exigências emocionais',
			polarity: 'demand',
			groupLabel: 'Cliente',
		},
		raw: 62.5,
		stressEq: 62.5,
		band: 'attention',
	},
	{
		dim: {
			id: 's6',
			shortLabel: 'Assédio / violência',
			polarity: 'demand',
			groupLabel: 'Cliente',
		},
		raw: 80,
		stressEq: 45,
		band: 'favorable',
	},
	{
		dim: {
			id: 's7',
			shortLabel: 'Sintomas de sono',
			polarity: 'demand',
			groupLabel: 'Pessoal',
		},
		raw: 45,
		stressEq: 80,
		band: 'attention',
	},
	{
		dim: {
			id: 's8',
			shortLabel: 'Papéis ambíguos',
			polarity: 'demand',
			groupLabel: 'Trabalho',
		},
		raw: 70,
		stressEq: 55,
		band: 'favorable',
	},
	{
		dim: {
			id: 's9',
			shortLabel: 'Reconhecimento',
			polarity: 'demand',
			groupLabel: 'Cliente',
		},
		raw: 40,
		stressEq: 85,
		band: 'attention',
	},
	{
		dim: {
			id: 's10',
			shortLabel: 'Comunidade no trabalho',
			polarity: 'demand',
			groupLabel: 'Cliente',
		},
		raw: 50,
		stressEq: 75,
		band: 'attention',
	},
	{
		dim: {
			id: 's11',
			shortLabel: 'Saúde percebida',
			polarity: 'demand',
			groupLabel: 'Pessoal',
		},
		raw: 75,
		stressEq: 50,
		band: 'favorable',
	},
	{
		dim: {
			id: 'r1',
			shortLabel: 'Apoio da chefia',
			polarity: 'resource',
			groupLabel: 'Cliente',
		},
		raw: 65,
		stressEq: 35,
		band: 'attention',
	},
	{
		dim: {
			id: 'r2',
			shortLabel: 'Clareza de papel',
			polarity: 'resource',
			groupLabel: 'Trabalho',
		},
		raw: 55,
		stressEq: 45,
		band: 'attention',
	},
	{
		dim: {
			id: 'r3',
			shortLabel: 'Justiça organizacional',
			polarity: 'resource',
			groupLabel: 'Cliente',
		},
		raw: 45,
		stressEq: 55,
		band: 'attention',
	},
	{
		dim: {
			id: 'r4',
			shortLabel: 'Confiança horizontal',
			polarity: 'resource',
			groupLabel: 'Cliente',
		},
		raw: 72.5,
		stressEq: 27.5,
		band: 'favorable',
	},
	{
		dim: {
			id: 'r5',
			shortLabel: 'Significado do trabalho',
			polarity: 'resource',
			groupLabel: 'Pessoal',
		},
		raw: 60,
		stressEq: 40,
		band: 'attention',
	},
	{
		dim: {
			id: 'r6',
			shortLabel: 'Autonomia',
			polarity: 'resource',
			groupLabel: 'Trabalho',
		},
		raw: 47.5,
		stressEq: 52.5,
		band: 'attention',
	},
	{
		dim: {
			id: 'r7',
			shortLabel: 'Possibilidade de desenvolvimento',
			polarity: 'resource',
			groupLabel: 'Trabalho',
		},
		raw: 77.5,
		stressEq: 22.5,
		band: 'favorable',
	},
	{
		dim: {
			id: 'r8',
			shortLabel: 'Predição social',
			polarity: 'resource',
			groupLabel: 'Cliente',
		},
		raw: 52.5,
		stressEq: 47.5,
		band: 'attention',
	},
	{
		dim: {
			id: 'r9',
			shortLabel: 'Qualidade da liderança',
			polarity: 'resource',
			groupLabel: 'Cliente',
		},
		raw: 67.5,
		stressEq: 32.5,
		band: 'favorable',
	},
	{
		dim: {
			id: 'r10',
			shortLabel: 'Apoio dos colegas',
			polarity: 'resource',
			groupLabel: 'Cliente',
		},
		raw: 75,
		stressEq: 25,
		band: 'favorable',
	},
	{
		dim: {
			id: 'r11',
			shortLabel: 'Variação do trabalho',
			polarity: 'resource',
			groupLabel: 'Trabalho',
		},
		raw: 40,
		stressEq: 60,
		band: 'attention',
	},
];

/** COPSOQ radar — escala 1–5 (maior = melhor). */
const MOCK_RADAR = [
	{
		subject: 'Exigências no trabalho',
		avaliacao: 2.4,
		zona: COPSOQ_ATTENTION_THRESHOLD,
	},
	{
		subject: 'Saúde e bem-estar',
		avaliacao: 3.2,
		zona: COPSOQ_ATTENTION_THRESHOLD,
	},
	{
		subject: 'Comportamentos ofensivos',
		avaliacao: 4.4,
		zona: COPSOQ_ATTENTION_THRESHOLD,
	},
	{
		subject: 'Ritmo de trabalho',
		avaliacao: 2.6,
		zona: COPSOQ_ATTENTION_THRESHOLD,
	},
	{
		subject: 'Reconhecimento',
		avaliacao: 3.5,
		zona: COPSOQ_ATTENTION_THRESHOLD,
	},
	{
		subject: 'Clareza de papel',
		avaliacao: 3,
		zona: COPSOQ_ATTENTION_THRESHOLD,
	},
	{
		subject: 'Apoio da chefia',
		avaliacao: 2.8,
		zona: COPSOQ_ATTENTION_THRESHOLD,
	},
	{
		subject: 'Comunidade social',
		avaliacao: 3.7,
		zona: COPSOQ_ATTENTION_THRESHOLD,
	},
];

/** CBI — escala 0–100 (maior = melhor). */
const MOCK_CBI_DIMENSIONS: ReadonlyArray<{
	label: string;
	score: number;
	ringColor: string;
	badge: string;
	badgeTone: 'amber' | 'danger' | 'low';
	impactTag?: string;
}> = [
	{
		label: 'Pessoal',
		score: 56,
		ringColor: '#059669',
		badge: 'Risco baixo',
		badgeTone: 'low',
		impactTag: 'Alto melhor',
	},
	{
		label: 'Trabalho',
		score: 38,
		ringColor: '#d97706',
		badge: 'Risco moderado',
		badgeTone: 'amber',
		impactTag: 'Alto melhor',
	},
	{
		label: 'Cliente',
		score: 62,
		ringColor: '#059669',
		badge: 'Risco baixo',
		badgeTone: 'low',
		impactTag: 'Alto melhor',
	},
];

const labelClass =
	'text-[10px] font-black uppercase tracking-widest text-stone-400';

const selectTriggerClass =
	'h-9 w-full rounded-lg border-stone-200 bg-stone-50 px-2.5 text-xs font-bold text-stone-600 focus:border-stone-400 focus:ring-0';

const DONUT = {
	r: 40,
	stroke: 10,
	progressColor: '#e11d74',
} as const;

function ParticipationDonut({ percent }: { percent: number }) {
	const c = 2 * Math.PI * DONUT.r;
	const dash = (Math.min(100, Math.max(0, percent)) / 100) * c;
	const display = Math.round(percent);

	return (
		<div
			className="relative h-[7.5rem] w-[7.5rem] shrink-0"
			aria-label={`Participação ${display}%`}
		>
			<svg
				className="h-full w-full -rotate-90"
				viewBox="0 0 100 100"
				aria-hidden
			>
				<circle
					cx="50"
					cy="50"
					r={DONUT.r}
					fill="none"
					className="stroke-stone-200"
					strokeWidth={DONUT.stroke}
				/>
				<circle
					cx="50"
					cy="50"
					r={DONUT.r}
					fill="none"
					stroke={DONUT.progressColor}
					strokeWidth={DONUT.stroke}
					strokeLinecap="round"
					strokeDasharray={`${dash} ${c}`}
				/>
			</svg>
			<div className="absolute inset-0 flex items-center justify-center">
				<span className="text-xl font-black tracking-tight text-stone-900">
					{display}%
				</span>
			</div>
		</div>
	);
}

function CbiScoreDonut({
	score,
	max,
	ringColor,
}: {
	score: number;
	max: number;
	ringColor: string;
}) {
	const r = 40;
	const stroke = 6;
	const c = 2 * Math.PI * r;
	const pct = Math.min(100, Math.max(0, (score / max) * 100));
	const dash = (pct / 100) * c;

	return (
		<div
			className="relative h-24 w-24 shrink-0 md:h-[6.25rem] md:w-[6.25rem] [&_svg]:outline-none"
			aria-label={`${score} de ${max}`}
			onMouseDown={(e) => e.preventDefault()}
		>
			<svg
				className="h-full w-full -rotate-90 outline-none focus:outline-none"
				viewBox="0 0 100 100"
				aria-hidden
			>
				<circle
					cx="50"
					cy="50"
					r={r}
					fill="none"
					className="stroke-stone-200"
					strokeWidth={stroke}
				/>
				<circle
					cx="50"
					cy="50"
					r={r}
					fill="none"
					stroke={ringColor}
					strokeWidth={stroke}
					strokeLinecap="round"
					strokeDasharray={`${dash} ${c}`}
				/>
			</svg>
			<div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
				<span
					className="text-2xl font-black tracking-tight md:text-[1.65rem]"
					style={{ color: ringColor }}
				>
					{score}
				</span>
				<span className="mt-0.5 text-[10px] font-bold text-stone-400">
					/{max}
				</span>
			</div>
		</div>
	);
}

function cbiBadgeClass(tone: 'amber' | 'danger' | 'low') {
	if (tone === 'amber') return 'bg-amber-100 text-amber-800';
	if (tone === 'danger') return 'bg-red-100 text-red-700';
	return 'bg-emerald-100 text-emerald-800';
}

function cbiSummaryToneFromScore(score: number): 'danger' | 'amber' | 'low' {
	if (score <= 25) return 'danger';
	if (score < 50) return 'amber';
	return 'low';
}

function cbiSummaryValueClass(tone: 'danger' | 'amber' | 'low') {
	if (tone === 'danger') return 'text-red-500';
	if (tone === 'amber') return 'text-amber-500';
	return 'text-emerald-600';
}

function cbiSummaryBarClass(tone: 'danger' | 'amber' | 'low') {
	if (tone === 'danger') return 'bg-red-500';
	if (tone === 'amber') return 'bg-amber-500';
	return 'bg-emerald-500';
}

function countBands(rows: readonly DemoHeatRow[], band: HeatCellBand) {
	return rows.filter((r) => r.band === band).length;
}

const DemonstracaoDashboardPage = () => {
	const navigate = useNavigate();
	const [selectedMonthId, setSelectedMonthId] = useState<string>(
		MOCK_MONTHS[0].monthId,
	);
	const [departmentId, setDepartmentId] = useState<DeptId>(ALL_DEPARTMENT_ID);
	const [openDeptModal, setOpenDeptModal] = useState(false);
	const [isIndicadoresOpen, setIsIndicadoresOpen] = useState(true);
	const [isPerfilOpen, setIsPerfilOpen] = useState(true);
	const [heatMapSortMode, setHeatMapSortMode] =
		useState<HeatMapSortMode>('criticalFirst');

	const activeDeptLabel = useMemo(() => {
		if (departmentId === ALL_DEPARTMENT_ID) return 'Todos';
		return MOCK_DEPARTMENTS.find((d) => d.id === departmentId)?.name ?? '—';
	}, [departmentId]);

	const monthLabel =
		MOCK_MONTHS.find((m) => m.monthId === selectedMonthId)?.label ?? '—';

	const visionLegendSolid = useMemo(
		() => `${monthLabel} · ${activeDeptLabel}`,
		[monthLabel, activeDeptLabel],
	);

	const heatMapRows = useMemo(
		() => sortHeatMapRows(MOCK_HEAT_MAP_BASE, heatMapSortMode),
		[heatMapSortMode],
	);

	const participationPercent = 67;
	const responseCount = 14;
	const participationTotal = 21;

	const criticalCount = countBands(MOCK_HEAT_MAP_BASE, 'critical');
	const attentionCount = countBands(MOCK_HEAT_MAP_BASE, 'attention');
	const okCount = countBands(MOCK_HEAT_MAP_BASE, 'favorable');
	const dimensionsTotal = MOCK_HEAT_MAP_BASE.length;

	const cbiScore = 42;
	const cbiTone = cbiSummaryToneFromScore(cbiScore);
	const cbiStatusLabel = 'Risco moderado';
	const cbiFooterNote = '14 resposta(s) consolidadas';

	return (
		<>
			<Helmet>
				<title>Plumaa · Demonstração — Painel da empresa</title>
				<meta name="robots" content="noindex,nofollow" />
			</Helmet>
			<section className="w-full animate-fade-in space-y-6 bg-stone-50/80 px-4 pb-14 pt-6 md:px-8 md:pb-20 md:pt-8">
				<div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-stone-300">
					<button
						type="button"
						onClick={() => navigate('/empresa')}
						className="text-stone-400 transition-colors hover:text-stone-700"
					>
						Empresas
					</button>
					<span className="text-stone-200">/</span>
					<span className="truncate text-stone-900">
						Painel da empresa
					</span>
				</div>

				<div className="space-y-1">
					<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
						<div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
							<button
								type="button"
								onClick={() => navigate(-1)}
								className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-600 shadow-sm transition-colors hover:bg-stone-50"
								title="Voltar"
								aria-label="Voltar"
							>
								<ArrowLeft className="h-4 w-4" />
							</button>

							<div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-teal-600 text-lg font-black text-white">
								S
							</div>

							<div className="min-w-0 flex-1">
								<PageTitle text="Sua empresa" />
							</div>
						</div>
					</div>
				</div>

				<div className="w-full rounded-2xl border border-stone-100 bg-white p-4 shadow-sm">
					<div className="flex flex-col gap-4 sm:grid sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-end">
						<div className="min-w-0">
							<p className={labelClass}>Avaliação e coleta</p>
							<p className="mt-1 text-sm font-bold leading-snug text-stone-900">
								Formulário
							</p>
						</div>
						<div className="w-full sm:w-[16rem] sm:justify-self-center sm:text-center">
							<p className={cn(labelClass, 'mb-1')}>
								Mês de referência
							</p>
							<Select
								value={selectedMonthId}
								onValueChange={(v) => setSelectedMonthId(v)}
							>
								<SelectTrigger className={selectTriggerClass}>
									<SelectValue>
										{MOCK_MONTHS.find(
											(m) =>
												m.monthId === selectedMonthId,
										)?.label ?? '—'}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									<SelectGroup>
										{MOCK_MONTHS.map((month) => (
											<SelectItem
												key={month.monthId}
												value={month.monthId}
											>
												{month.label}
											</SelectItem>
										))}
									</SelectGroup>
								</SelectContent>
							</Select>
						</div>
						<div className="sm:justify-self-end">
							<button
								type="button"
								disabled
								title="Demonstração estática — exportação desativada."
								className="inline-flex shrink-0 cursor-not-allowed items-center gap-2 self-start rounded-xl bg-[#e11d74]/60 px-2.5 py-2 text-[11px] font-bold uppercase tracking-wide text-white opacity-80 shadow-sm"
							>
								<Download className="size-3.5" aria-hidden />
								Baixar relatório
							</button>
						</div>
					</div>

					<div className="mt-4 space-y-3 border-t border-stone-100 pt-4">
						<SelectionRowTrigger
							label="Departamento"
							displayText={
								departmentId === ALL_DEPARTMENT_ID
									? 'Todos'
									: activeDeptLabel
							}
							placeholder="Selecione o departamento"
							onOpen={() => setOpenDeptModal(true)}
						/>
					</div>

					<div className="mt-4 border-t border-stone-100 pt-3">
						<p className="text-[11px] font-medium leading-relaxed text-stone-400">
							Coleta iniciada em 01/04/2026 - Coleta encerrada em
							28/04/2026
						</p>
					</div>
				</div>

				<DashboardDepartmentPickerModal
					isOpen={openDeptModal}
					onClose={() => setOpenDeptModal(false)}
					departments={MOCK_DEPARTMENTS}
					isLoading={false}
					selectedId={departmentId}
					onSelect={(depId) => setDepartmentId(depId as DeptId)}
				/>

				<div className="w-full overflow-visible rounded-2xl border border-stone-100 bg-white shadow-sm">
					<div className="flex w-full items-center justify-between gap-4 px-4 py-5 md:px-6">
						<span className="text-base font-bold tracking-tight text-stone-900 md:text-lg">
							{COPY.resumo.indicatorsSectionTitle}
						</span>
						<button
							type="button"
							onClick={() => setIsIndicadoresOpen((v) => !v)}
							className="-m-1 rounded-lg p-1 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-300"
							aria-expanded={isIndicadoresOpen}
							aria-label={
								isIndicadoresOpen
									? 'Recolher indicadores'
									: 'Expandir indicadores'
							}
						>
							<ChevronDown
								className={cn(
									'size-5 transition-transform duration-300',
									isIndicadoresOpen
										? 'rotate-180'
										: 'rotate-0',
								)}
							/>
						</button>
					</div>

					<div
						className={cn(
							'overflow-hidden transition-all duration-300 ease-in-out',
							isIndicadoresOpen
								? 'max-h-[4800px] opacity-100'
								: 'max-h-0 opacity-0',
						)}
					>
						<div className="grid grid-cols-1 items-stretch gap-6 px-4 pb-6 pt-2 md:px-6 md:[grid-template-columns:6fr_13fr_13fr]">
							<div className="flex min-w-0 flex-col gap-3 rounded-2xl border border-stone-100 bg-white p-4 shadow-sm">
								<p className={cn(labelClass, 'leading-tight')}>
									{COPY.resumo.participacao.cardTitle}
								</p>
								<div className="flex min-h-0 flex-1 items-center justify-center py-1">
									<ParticipationDonut
										percent={participationPercent}
									/>
								</div>
								<p className="text-center text-sm font-bold tabular-nums tracking-tight text-stone-900">
									{responseCount} de {participationTotal}{' '}
									colaboradores responderam
								</p>
								<p className="text-center text-[11px] font-medium leading-snug text-stone-500">
									Percentual de participação no recorte atual.
								</p>
							</div>

							<div className="flex min-w-0 flex-col gap-2 rounded-2xl border border-stone-100 bg-white p-4 shadow-sm">
								<p
									className={cn(
										labelClass,
										'break-words leading-tight',
									)}
								>
									{COPY.resumo.riskFactors.title}
								</p>
								<div className="flex flex-wrap items-baseline gap-2 leading-none">
									<span className="text-4xl font-black tracking-tight text-[#e11d74] md:text-5xl">
										{criticalCount}
									</span>
									<span className="text-lg font-bold text-stone-600 md:text-xl">
										{COPY.resumo.riskFactors.critical.label}
									</span>
								</div>
								<div className="flex flex-wrap gap-1.5">
									<span className="inline-flex items-center gap-1.5 rounded-full bg-rose-100 px-2.5 py-1 text-[11px] font-bold text-rose-700">
										<span
											className="h-1.5 w-1.5 shrink-0 rounded-full bg-rose-600"
											aria-hidden
										/>
										{criticalCount}{' '}
										{COPY.resumo.riskFactors.critical.label}
									</span>
									<span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold text-amber-800">
										<span
											className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
											aria-hidden
										/>
										{attentionCount}{' '}
										{
											COPY.resumo.riskFactors.attention
												.label
										}
									</span>
									<span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-800">
										<span
											className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-600"
											aria-hidden
										/>
										{okCount}{' '}
										{COPY.resumo.riskFactors.ok.label}
									</span>
								</div>
								<div className="flex h-2.5 w-full overflow-hidden rounded-full bg-stone-100">
									{criticalCount > 0 ? (
										<div
											className="h-full bg-rose-600"
											style={{
												width: `${
													(criticalCount /
														dimensionsTotal) *
													100
												}%`,
											}}
										/>
									) : null}
									<div
										className="h-full bg-amber-400"
										style={{
											width: `${
												(attentionCount /
													dimensionsTotal) *
												100
											}%`,
										}}
									/>
									<div
										className="h-full bg-emerald-500"
										style={{
											width: `${
												(okCount / dimensionsTotal) *
												100
											}%`,
										}}
									/>
								</div>
								<p className="text-[11px] font-medium leading-snug text-stone-400">
									de {dimensionsTotal} dimensões avaliadas
								</p>
							</div>

							<div className="flex min-w-0 flex-col gap-2 rounded-2xl border border-stone-100 bg-white p-4 shadow-sm">
								<p
									className={cn(
										labelClass,
										'break-words leading-tight',
									)}
								>
									{COPY.resumo.burnoutCbi.title}
								</p>
								<div className="flex flex-wrap items-baseline gap-1.5 leading-none">
									<span
										className={cn(
											'text-4xl font-black tracking-tight md:text-5xl',
											cbiSummaryValueClass(cbiTone),
										)}
									>
										{cbiScore}
									</span>
									<span className="text-lg font-bold text-stone-600 md:text-xl">
										/{COPY.resumo.burnoutCbi.scaleMax}
									</span>
								</div>
								<span
									className={cn(
										'w-fit rounded-full px-2.5 py-1 text-[11px] font-bold',
										cbiBadgeClass(cbiTone),
									)}
								>
									{cbiStatusLabel}
								</span>
								<div className="h-2.5 w-full overflow-hidden rounded-full bg-stone-200">
									<div
										className={cn(
											'h-full rounded-full',
											cbiSummaryBarClass(cbiTone),
										)}
										style={{
											width: `${
												(cbiScore /
													COPY.resumo.burnoutCbi
														.scaleMax) *
												100
											}%`,
										}}
									/>
								</div>
								<p className="text-[11px] font-medium leading-relaxed text-stone-500">
									{COPY.resumo.burnoutCbi.scaleHint}
								</p>
								<p className="text-[11px] font-medium leading-snug text-stone-400">
									{cbiFooterNote}
								</p>
							</div>
						</div>
					</div>
				</div>

				<div className="w-full overflow-hidden rounded-2xl border border-stone-100 bg-white shadow-sm">
					<div className="flex w-full items-center justify-between gap-3 px-4 py-4 md:px-6">
						<span className="min-w-0 text-base font-bold tracking-tight text-stone-900 md:text-lg">
							{COPY.perfil.sectionTitle}
						</span>
						<button
							type="button"
							onClick={() => setIsPerfilOpen((v) => !v)}
							className="-m-1 shrink-0 rounded-lg p-1 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-300"
							aria-expanded={isPerfilOpen}
							aria-label={
								isPerfilOpen
									? 'Recolher perfil psicossocial'
									: 'Expandir perfil psicossocial'
							}
						>
							<ChevronDown
								className={cn(
									'size-5 transition-transform duration-300',
									isPerfilOpen ? 'rotate-180' : 'rotate-0',
								)}
							/>
						</button>
					</div>

					<div
						className={cn(
							'overflow-hidden transition-all duration-300 ease-in-out',
							isPerfilOpen
								? 'max-h-[2800px] opacity-100'
								: 'max-h-0 opacity-0',
						)}
					>
						<div className="grid grid-cols-1 items-stretch gap-4 px-4 pb-6 pt-0 md:grid-cols-2 md:gap-5 md:px-6">
							<div className="flex min-h-0 flex-col rounded-2xl border border-stone-100 bg-white p-3 shadow-sm md:p-4">
								<p className={cn(labelClass, 'leading-tight')}>
									{COPY.perfil.visionByDomain.kicker}
								</p>
								<h3 className="pt-1 text-sm font-bold text-stone-900 md:text-base">
									{COPY.perfil.visionByDomain.cardTitle}
								</h3>
								<p className="whitespace-pre-line text-[11px] font-medium leading-snug text-stone-500 md:text-xs">
									{COPY.perfil.visionByDomain.subtitle}
								</p>
								<div
									className="mt-1 min-h-[220px] w-full shrink-0 md:min-h-[240px] [&_.recharts-wrapper]:outline-none"
									onMouseDown={(e) => e.preventDefault()}
									role="presentation"
								>
									<ResponsiveContainer
										width="100%"
										height="100%"
									>
										<RadarChart
											data={MOCK_RADAR}
											outerRadius="72%"
											innerRadius="12%"
										>
											<PolarGrid stroke="#e7e5e4" />
											<PolarAngleAxis
												dataKey="subject"
												tick={{
													fill: '#78716c',
													fontSize: 10,
												}}
											/>
											<PolarRadiusAxis
												domain={[1, 5]}
												tickCount={5}
												tick={{
													fill: '#a8a29e',
													fontSize: 9,
												}}
											/>
											<Radar
												name="avaliacao"
												dataKey="avaliacao"
												stroke={COPSOQ_ACCENT}
												fill={COPSOQ_ACCENT}
												fillOpacity={0.35}
												strokeWidth={2}
											/>
											<Radar
												name="zona"
												dataKey="zona"
												stroke={COPSOQ_ACCENT}
												fill="none"
												strokeWidth={2}
												strokeDasharray="4 3"
											/>
										</RadarChart>
									</ResponsiveContainer>
								</div>
								<div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11px] font-semibold text-stone-600">
									<span className="flex items-center gap-1.5">
										<span
											className="h-2 w-2 shrink-0 rounded-full"
											style={{
												backgroundColor: COPSOQ_ACCENT,
											}}
											aria-hidden
										/>
										{visionLegendSolid}
									</span>
									<span className="flex items-center gap-1.5">
										<span
											className="h-2 w-2 shrink-0 rounded-full border-2 border-[#e11d74] bg-transparent"
											aria-hidden
										/>
										Limiar atenção (2,34)
									</span>
								</div>
							</div>

							<div className="flex min-h-0 flex-col rounded-2xl border border-stone-100 bg-white p-4 shadow-sm md:p-5">
								<h3 className="text-sm font-bold text-stone-900 md:text-base">
									{COPY.perfil.cbi.title}
								</h3>
								<p className="mt-0.5 text-[11px] font-medium leading-snug text-stone-500 md:text-xs">
									{COPY.perfil.cbi.subtitle}
								</p>
								<p className="mt-2 text-[11px] font-medium leading-snug text-stone-600 md:text-xs">
									<span className="inline-flex rounded-full border border-stone-200 bg-stone-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.04em] text-stone-600">
										Alto melhor
									</span>{' '}
									(risco): ≥50 baixo · 26–49 moderado · ≤25
									alto.
								</p>
								<div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
									{MOCK_CBI_DIMENSIONS.map((dim) => (
										<div
											key={dim.label}
											className="flex flex-col items-center gap-2 rounded-xl border border-stone-200 bg-white px-3 py-3 text-center shadow-sm"
										>
											{dim.impactTag ? (
												<span className="rounded-full border border-stone-200 bg-stone-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.04em] text-stone-600">
													{dim.impactTag}
												</span>
											) : null}
											<CbiScoreDonut
												score={dim.score}
												max={100}
												ringColor={dim.ringColor}
											/>
											<span className="text-sm font-bold text-stone-900">
												{dim.label}
											</span>
											<span
												className={cn(
													'rounded-full px-2.5 py-1 text-[11px] font-bold',
													cbiBadgeClass(
														dim.badgeTone,
													),
												)}
											>
												{dim.badge}
											</span>
										</div>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="w-full overflow-hidden rounded-2xl border border-stone-100 bg-white shadow-sm">
					<div className="px-4 pb-2 pt-4 md:px-6 md:pt-5">
						<h2 className="text-base font-bold tracking-tight text-stone-900 md:text-lg">
							{COPY.mapaCalor.sectionTitle}
						</h2>
					</div>

					<div className="px-4 pb-6 pt-0 md:px-6">
						<div className="mt-5 rounded-xl border border-stone-100 bg-white pb-4 pl-3 pr-3 pt-3 shadow-sm md:pl-4">
							<p className="text-xs font-bold text-stone-900">
								{COPY.mapaCalor.blockTitle}
							</p>
							<div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-x-5">
								{(
									[
										[
											'favorable',
											COPY.mapaCalor.legendFavorable,
										],
										[
											'attention',
											COPY.mapaCalor.legendAttention,
										],
										[
											'critical',
											COPY.mapaCalor.legendCritical,
										],
									] as const
								).map(([key, name]) => (
									<span
										key={key}
										className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-stone-800"
									>
										<span
											className={cn(
												'h-2.5 w-8 shrink-0 rounded border',
												HEAT_LEGEND[key].swatchClass,
											)}
											aria-hidden
										/>
										<span>{name}</span>
										<span className="font-semibold normal-case tabular-nums text-stone-600">
											{HEAT_LEGEND[key].range}
										</span>
									</span>
								))}
							</div>
							<div className="mt-3 flex items-center justify-end">
								<div className="w-full max-w-[16rem] sm:w-[16rem]">
									<Select
										value={heatMapSortMode}
										onValueChange={(v) =>
											setHeatMapSortMode(
												v as HeatMapSortMode,
											)
										}
									>
										<SelectTrigger
											className={selectTriggerClass}
										>
											<SelectValue>
												{
													HEAT_MAP_SORT_LABELS[
														heatMapSortMode
													]
												}
											</SelectValue>
										</SelectTrigger>
										<SelectContent>
											<SelectGroup>
												<SelectItem value="criticalFirst">
													Ordenação: críticos primeiro
												</SelectItem>
												<SelectItem value="favorableFirst">
													Ordenação: favoráveis
													primeiro
												</SelectItem>
											</SelectGroup>
										</SelectContent>
									</Select>
								</div>
							</div>
							<div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
								{heatMapRows.map(({ dim, raw, band }) => (
									<div
										key={dim.id}
										className={cn(
											'relative flex min-h-[4.5rem] flex-col justify-between rounded-xl border p-2.5 text-left',
											copsoqHeatCellClass(band),
										)}
									>
										<span className="absolute right-2 top-2 shrink-0">
											<Tooltip
												position="top"
												tapToPinOnTouch
												content={`Esta subcategoria pertence a: ${dim.groupLabel}.`}
												className="max-w-72 whitespace-normal border border-stone-200 bg-white text-[10px] font-medium leading-snug text-stone-700 shadow-md"
											>
												<button
													type="button"
													className="rounded-full p-0.5 text-stone-500 transition-colors hover:bg-stone-200/60 hover:text-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-300"
													aria-label={`Origem da subcategoria ${dim.shortLabel}`}
												>
													<Info className="size-3" />
												</button>
											</Tooltip>
										</span>
										<span className="text-lg font-black tabular-nums leading-none">
											{formatHeatMapScore(raw)}
										</span>
										<span className="mt-0.5 pr-4 text-[10px] font-bold leading-tight">
											{dim.shortLabel}
										</span>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>

				<p className="text-center text-[10px] font-medium text-stone-400">
					Demonstração estática — sem dados reais da API.
				</p>
			</section>
		</>
	);
};

export { DemonstracaoDashboardPage };
